/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Heading, Text } from '@towersystems/towersystems-design-system';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Card from 'components/card';
import AnnotatedSection from 'components/annotated-section';
import FormInputCheckbox from 'components/forms/form-checkbox';
import FormTextInput from 'components/forms/form-text-input';
import FormNumberInput from 'components/forms/form-number-input';
import FormLabel from 'components/forms/form-label';

const MagentoPreferences = ({ saveConfigValue, saveConfigValueInput, values }) => {
    return (
        <Stack space='xlarge' dividers>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Product'
                    description='Choose your preferences when sending product data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='xlarge'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product title'
                                    onChange={saveConfigValue}
                                    name='updateProductName'
                                    checked={get(values, 'updateProductName', false)}
                                >
                                    When ticked the Description from Retailer will update Product 
                                    Name/Title in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product description'
                                    onChange={saveConfigValue}
                                    name='updateProductDescription'
                                    checked={get(values, 'updateProductDescription', false)}
                                >
                                    When ticked the Web Description from Retailer will be used to 
                                    update Description in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product attributes'
                                    onChange={saveConfigValue}
                                    name='updateProductAttributes'
                                    checked={get(values, 'updateProductAttributes', false)}
                                >
                                    When ticked, attributes from Retailer will be used to update attributes.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product weight'
                                    onChange={saveConfigValue}
                                    name='updateProductWeight'
                                    checked={get(values, 'updateProductWeight', false)}
                                >
                                    When ticked the product weight and unit field from Retailer will 
                                    update the product weight filed in your ecommerce platform. 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product price'
                                    onChange={saveConfigValue}
                                    name='updateProductPrice'
                                    checked={get(values, 'updateProductPrice', false)}
                                >
                                    When ticked the chosen price from Retailer (see inventory and pricing below) 
                                    will be used to update the product price in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product categories'
                                    onChange={saveConfigValue}
                                    name='updateProductCategories'
                                    checked={get(values, 'updateProductCategories', false)}
                                >
                                    When ticked, categories and departments from Retailer will be updated on products.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Department'
                    description='Choose your preferences when sending department data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section>
                            <FormInputCheckbox
                                label='Update department title'
                                onChange={saveConfigValue}
                                name='updateDepartmentName'
                                checked={get(values, 'updateDepartmentName', false)}
                            >
                                When ticked the Department Name from Retailer will be used to 
                                update the corresponding Category in your ecommerce platform.
                            </FormInputCheckbox>
                        </Card.Section>
                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Category'
                    description='Choose your preferences when sending category data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section>
                            <FormInputCheckbox
                                label='Update category title'
                                onChange={saveConfigValue}
                                name='updateCategoryName'
                                checked={get(values, 'updateCategoryName', false)}
                            >
                                When ticked the Category Name from Retailer will be used to 
                                update the corresponding Category in your ecommerce platform.
                            </FormInputCheckbox>
                        </Card.Section>
                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Inventory & Pricing'
                    description='Choose your preferences when sending inventory and pricing data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='large'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product inventory levels'
                                    onChange={saveConfigValue}
                                    name='updateInventory'
                                    checked={get(values, 'updateInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer 
                                    will be used as the inventory level in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='TWC Track product inventory levels'
                                    onChange={saveConfigValue}
                                    name='trackInventory'
                                    checked={get(values, 'trackInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be subtracted 
                                    by the reported, but not yet imported, TWC sales 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Round down QOH To Whole Number'
                                    onChange={saveConfigValue}
                                    name='roundDownInventory'
                                    checked={get(values, 'roundDownInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be 
                                    rounded down in your ecommerce platform if they are in decimal
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use static quantity'
                                    onChange={saveConfigValue}
                                    name='useStaticQuantity'
                                    checked={get(values, 'useStaticQuantity', false)}
                                >
                                    When ticked, if the StaticQuantity received from Retailer is true, the inventory for the product will not be tracked in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use web price'
                                    onChange={saveConfigValue}
                                    name='useWebPrice'
                                    checked={get(values, 'useWebPrice', false)}
                                >
                                    When ticked the Retailer Web Price will be used as the sell price of the item.  
                                    If unticked the system will use the Retailer Retail Price.  
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use catalogue price'
                                    onChange={saveConfigValue}
                                    name='useCataloguePrice'
                                    checked={get(values, 'useCataloguePrice', false)}
                                >
                                    When ticked, the Retailer Stock Item Catalogue price will be used in Magento 
                                    as the Retail Price.  The regular price, either the web or retail price, will be set as the compare at price.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use trade price'
                                    onChange={saveConfigValue}
                                    name='useStockTrade'
                                    checked={get(values, 'useStockTrade', false)}
                                >
                                When ticked, StockTrade price from Retailer will be used for Wholesale customer group.
                                </FormInputCheckbox>
                                <Box className="card__section-tradeprice">
                                    <FormLabel>Website ID using Trade Price:</FormLabel>
                                    <FormTextInput name='stockTradeWebsiteId' onChange={saveConfigValueInput} value={get(values, 'stockTradeWebsiteId')} />
                                    <Text className="form-text-input__additional-content">When leave blank, StockTrade price wil be applied to all websites. 
                                    If you would like to use trade price in specified websites, please use comma to 
                                    seperate each website id.</Text>
                                </Box>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <Box className="card__section-outofstockqty">
                                    <FormLabel>Out of Stock Quantity:</FormLabel>
                                    <FormNumberInput name='outOfStockQty' onChange={saveConfigValueInput} value={get(values, 'outOfStockQty')} />
                                    <Text className="form-text-input__additional-content">The Quantity entered here will be used as out of stock quantity. 
                                    Any qty below this value will be marked as out of stock on your ecommerce platform.</Text>
                                </Box>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Unpublish out of stock'
                                    onChange={saveConfigValue}
                                    name='unpublishOutOfStock'
                                    checked={get(values, 'unpublishOutOfStock', false)}
                                >
                                    When ticked, products with chosen inventory will be unpublished or 
                                    unavailable for purchase on your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>
        </Stack>
    );
};

export default connect((state, ownProps) => ({}), {})(MagentoPreferences);
